import { useMemo, useState, useEffect, useRef, type ReactNode, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Brain, Bolt, Activity } from 'lucide-react';
import { AuthModal } from '@/components/auth/AuthModal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/stores/authStore';
import screenApp1 from '@/assets/sc_app_1_1.PNG';
import screenApp2 from '@/assets/sc_app_1_2.PNG';
import screenApp3 from '@/assets/sc_app_1_3.PNG';
import screenApp4 from '@/assets/sc_app_1_4.PNG';
import screenApp5 from '@/assets/sc_app_1_5.PNG';

const PRODUCT_SCREENS = [
  {
    src: screenApp1,
    alt: 'GymAhead app home with workouts and quick actions',
  },
  {
    src: screenApp2,
    alt: 'GymAhead workout detail with exercise list',
  },
  {
    src: screenApp3,
    alt: 'GymAhead workout',
  },
  {
    src: screenApp4,
    alt: 'GymAhead workout',
  },
  {
    src: screenApp5,
    alt: 'GymAhead workout',
  }
];

const COPY = {
  brand: 'gymAhead',
  heroHeadline: `Training that keeps up with your life.`,
  heroSubheadline:
    'Adaptive workouts for busy people who already go to the gym.',
  heroDescription:
    'Beahead Gym creates a workout plan that adjusts to your real week - so you never have to decide what to do when you walk into the gym.',
  northStar: "You don't need more motivation. You need fewer decisions.",
  heroCtaPrimary: 'Join the beta',
  heroCtaSecondary: 'See how it works',
  beta: {
    linkText: 'Running a small closed beta - apply for early access.',
    title: 'Join the beta',
    intro:
      'We’re running a small, invite-only beta with people who want to help shape a calm, long-term training system.',
    bullets: [
      'Limited seats',
      'Direct feedback channel',
      'Beta users get early access (planned price: €4.99/month). No credit card required during beta.',
    ],
    cta: 'Apply for beta access',
    successTitle: 'Thanks for signing up! We will reach out if a spot opens.',
    successBody: 'No promises or dates, just a calm queue.',
    form: {
      emailLabel: 'Email',
      frequencyLabel: 'Training frequency',
      frequencyOptions: ['0–1 per week', '2–3 per week', '4+ per week'],
      rhythmLabel: 'What usually breaks your rhythm?',
      rhythmOptions: ['Work stress', 'Travel', 'Low energy', 'Illness', 'Other'],
      goalLabel: 'What do you want gymAhead to help with? (optional)',
      consentLabel: 'I agree to be contacted about the beta.',
      submitLabel: 'Submit application',
    },
  },
  audience: {
    title: 'For gym-goers who want fewer decisions, not less control',
    subtitle: 'gymAhead helps you show up and train with a plan that’s ready by default and adjustable when you want. Especially when life makes consistency harder than it should be.',
    disclaimer: '',
  },
  valueProps: {
    title: '',
    items: [
      {
        title: 'No planning, no rebuilding',
        description: 'Miss a workout and the plan adjusts automatically.',
        icon: Brain,
      },
      {
        title: 'No decision fatigue',
        description: "You always know what today's workout is before you enter the gym.",
        icon: Bolt,
      },
      {
        title: 'Built to sustain rhythm and progress',
        description: 'Meetings, travel, low-energy days - gymAhead adapts to life circumstances.',
        icon: Activity,
      },
    ],
  },
  problem: {
    title: 'Perfect plans don’t survive real weeks.',
    bullets: [
      'You lose momentum after a break and don’t know how to restart',
      'On bad days you either skip - or overdo it',
      'Mobility “should” happen, but it never fits the day',
      'The hardest part isn’t lifting. It’s deciding',
    ],
    close: 'gymAhead removes the decision tax.',
  },
  pillars: [
    {
      title: 'Less thinking. More showing up.',
      description:
        'You don’t plan your training every week. gymAhead does - based on your goal and actual rhythm.',
    },
    {
      title: 'Adjust - only when change is needed.',
      description:
        'Breaks or chaotic weeks trigger smart volume and intensity adjustments.',
      bullets: [
        'Break-aware restart',
        'Low-energy day option',
        'Auto “back on track” ramp',
      ],
    },
    {
      title: 'Consistency that survives real life.',
      description:
        'Stable plans + weekly direction so progress doesn’t depend on perfect weeks.',
    },
  ],
  busyWeeks: {
    title: 'Built for busy weeks.',
    cards: [
      {
        title: 'Back-in-rhythm mode',
        description:
          'Took days (or weeks) off? Get a safe, effective ramp back automatically.',
      },
      {
        title: '“I don’t feel it today” workout',
        description:
          'Short, focused sessions that still move your goal forward.',
      },
      {
        title: 'Mini mobility sessions',
        description:
          '5–8 minute add-ons to counter stiffness and mobility limitations - no extra planning.',
      },
    ],
    close: 'No guilt. No punishment. Just intelligent course correction.',
  },
  howItWorks: [
    {
      title: 'You set goals & constraints',
      description: 'Tell us your training goals and schedule preferences once.',
    },
    {
      title: 'The system creates a base plan',
      description: 'We build a workout plan tailored to your goals and rhythm.',
    },
    {
      title: 'The plan adapts week by week when life happens',
      description: 'Miss a workout? Low energy? Travel? The plan recalculates automatically.',
    },
  ],
  proof: {
    title: 'Built on proven training principles',
    description: 'gymAhead uses progressive overload and autoregulation to keep you progressing safely, even when life gets busy.',
    workoutExample: {
      title: 'Example workout',
      description: 'A typical session might include: Squat 3x5, Bench Press 3x5, Rows 3x8. If you miss this workout, the next session automatically adjusts volume and intensity.',
    },
  },
  whatThisIs: {
    title: "What this is (and isn't)",
    leftTitle: 'Not this',
    rightTitle: 'This',
    contrasts: [
      'Not rigid plans that break the moment life changes',
      'Not endless choices or constant re-planning',
      'Not for beginners who need basic guidance',
    ],
    affirmations: [
      'A training system that adapts when you miss workouts',
      'A clear default path - you always know what to do',
      'Built for people who already train and want fewer decisions',
    ]
  },
  pricing: {
    title: '',
    price: '€4.99 / month',
    support: 'Everything included - Cancel anytime',
    supportSecondary: '',
    features: [
      'Training plans that don’t require weekly decision-making',
      'Break-aware adjustments to get back into rhythm',
      '“Low energy” quick workouts',
      'Mini mobility add-ons',
      'Progress tracking with weekly summaries',
      'Cloud sync across devices',
      'Continuous improvements included',
    ],
    cta: 'Start for €4.99',
  },
  faq: [
    {
      question: 'Is this for beginners?',
      answer:
        'It’s for people who already train or want a simple, consistent system.',
    },
    {
      question: 'What if I miss a week?',
      answer:
        'You get a ramp-back adjustment so you’re safely back in rhythm fast.',
    },
    {
      question: 'What if I’m low energy?',
      answer: 'Choose a quick workout that still moves your goal forward.',
    },
    {
      question: 'Is there an AI chat?',
      answer:
        'No. The intelligence is embedded and shows up when it matters.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes. You can cancel whenever you want.',
    },
  ],
};

type TrackEventName =
  | 'cta_hero_primary_click'
  | 'cta_pricing_click'
  | 'nav_how_it_works_click';

const trackEvent = (eventName: TrackEventName) => {
  if (typeof window === 'undefined') return;
  const analytics = (
    window as typeof window & { analytics?: { track?: (name: string) => void } }
  ).analytics;
  if (analytics?.track) {
    analytics.track(eventName);
  }
};

const scrollToId = (id: string) => {
  if (typeof document === 'undefined') return;
  const target = document.getElementById(id);
  if (!target) return;

  // Get the header height (fixed header with border)
  const header = document.querySelector('header');
  const headerHeight = header ? header.offsetHeight : 80;

  // Calculate the position accounting for the fixed header
  const elementPosition = target.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

type SectionProps = {
  id?: string;
  title?: string;
  eyebrow?: string;
  children: ReactNode;
  className?: string;
};

const Section = ({ id, title, eyebrow, children, className }: SectionProps) => (
  <section id={id} className={`py-20 px-6 ${className ?? ''}`}>
    <div className="mx-auto w-full max-w-5xl">
      {(title || eyebrow) && (
        <div className="mb-12 text-center">
          {eyebrow && (
            <p className="text-sm uppercase tracking-[0.2em] text-neutral-400">
              {eyebrow}
            </p>
          )}
          {title && (
            <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-neutral-900">
              {title}
            </h2>
          )}
        </div>
      )}
      {children}
    </div>
  </section>
);

type CardProps = {
  title: string;
  description?: string;
  children?: ReactNode;
};

const Card = ({ title, description, children }: CardProps) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
    <h3 className="text-xl font-semibold text-neutral-900">{title}</h3>
    {description && (
      <p className="mt-3 text-base text-neutral-600">{description}</p>
    )}
    {children}
  </div>
);

const Navbar = ({
  onStart,
  onLogin,
  onEnterApp,
  isAuthenticated,
}: {
  onStart: () => void;
  onLogin: () => void;
  onEnterApp: () => void;
  isAuthenticated: boolean;
}) => (
  <header className="fixed left-0 right-0 top-0 z-40 border-b border-neutral-100 bg-white/80 backdrop-blur">
    <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-4">
      <div 
        className="text-lg text-neutral-900" 
        style={{ fontWeight: 600, letterSpacing: '-0.02em' }}
      >
        {COPY.brand}
      </div>
      <nav className="hidden items-center gap-6 text-sm text-neutral-600 md:flex">
        <a
          href="#value-props"
          className="transition-colors hover:text-neutral-900"
          onClick={(event) => {
            event.preventDefault();
            scrollToId('value-props');
          }}
        >
          Value proposition
        </a>
        <a
          href="#how-it-works"
          className="transition-colors hover:text-neutral-900"
          onClick={(event) => {
            event.preventDefault();
            trackEvent('nav_how_it_works_click');
            scrollToId('how-it-works');
          }}
        >
          How it works
        </a>
        <a
          href="#pricing"
          className="transition-colors hover:text-neutral-900"
          onClick={(event) => {
            event.preventDefault();
            scrollToId('pricing');
          }}
        >
          Pricing
        </a>
      </nav>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded-full bg-neutral-900 px-5 py-2 text-sm font-medium text-white transition hover:bg-neutral-800"
          onClick={onStart}
        >
          Start training
        </button>
        <button
          type="button"
          className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 transition hover:border-neutral-300 hover:text-neutral-900"
          onClick={isAuthenticated ? onEnterApp : onLogin}
        >
          {isAuthenticated ? 'Go to app' : 'Log in'}
        </button>
      </div>
    </div>
  </header>
);

const Hero = () => {
  const navigate = useNavigate();
  const [activeScreen, setActiveScreen] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const screen = PRODUCT_SCREENS[activeScreen] ?? PRODUCT_SCREENS[0];

  // Auto-rotate carousel every 4 seconds
  useEffect(() => {
    if (isPaused) return;

    intervalRef.current = setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % PRODUCT_SCREENS.length);
    }, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused]);

  return (
    <section className="pt-32 pb-20 px-6">
      <div className="mx-auto grid w-full max-w-5xl grid-cols-1 items-center gap-12 md:grid-cols-[1.1fr_0.9fr]">
        <div>
          {/* <p className="text-sm uppercase tracking-[0.25em] text-neutral-400">
          {COPY.priceLine}
        </p> */}
          <h1 className="mt-4 whitespace-pre-line text-4xl font-semibold text-neutral-900 md:text-5xl">
            {COPY.heroHeadline}
          </h1>
          <p className="mt-3 text-xl font-medium text-neutral-700 md:text-2xl">
            {COPY.heroSubheadline}
          </p>
          <p className="mt-5 text-lg text-neutral-600">
            {COPY.heroDescription}
          </p>
          <p className="mt-6 text-base font-medium text-neutral-900 italic">
            {COPY.northStar}
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button
              type="button"
              className="rounded-full bg-neutral-900 px-6 py-3 text-base font-medium text-white transition hover:bg-neutral-800"
              onClick={() => {
                trackEvent('cta_hero_primary_click');
                navigate('/beta');
              }}
            >
              {COPY.heroCtaPrimary}
            </button>
            <button
              type="button"
              className="rounded-full border border-neutral-200 px-6 py-3 text-base font-medium text-neutral-700 transition hover:border-neutral-300 hover:text-neutral-900"
              onClick={() => {
                trackEvent('nav_how_it_works_click');
                scrollToId('how-it-works');
              }}
            >
              {COPY.heroCtaSecondary}
            </button>
          </div>
        </div>
        <div className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm">
          <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4">
            <div
              className="rounded-2xl overflow-hidden flex items-center justify-center"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <img
                src={screen.src}
                alt={screen.alt}
                className="mx-auto h-auto w-auto rounded-xl object-contain transition-opacity duration-500"
                style={{
                  imageRendering: '-webkit-optimize-contrast',
                  // maxHeight: '600px',
                  // width: 'auto',
                }}
                loading="eager"
              />
            </div>
            <div className="mt-4 flex items-center justify-center gap-2">
              {PRODUCT_SCREENS.map((_, index) => (
                <button
                  key={`screen-${index}`}
                  type="button"
                  aria-label={`Show screen ${index + 1}`}
                  className={`h-2.5 w-2.5 rounded-full transition ${index === activeScreen
                    ? 'bg-neutral-900'
                    : 'bg-neutral-300'
                    }`}
                  onClick={() => setActiveScreen(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ onStart }: { onStart: () => void }) => (
  <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-sm">
    <div className="flex flex-col gap-3">
      <h3 className="text-2xl font-semibold text-neutral-900">
        {COPY.pricing.title}
      </h3>
      <p className="text-3xl font-semibold text-neutral-900">
        {COPY.pricing.price}
      </p>
      <div className="text-sm text-neutral-500">
        <p>{COPY.pricing.support}</p>
        <p>{COPY.pricing.supportSecondary}</p>
      </div>
    </div>
    <ul className="mt-6 space-y-3 text-neutral-600">
      {COPY.pricing.features.map((feature) => (
        <li key={feature} className="flex items-start gap-3">
          <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-neutral-400" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
    <button
      type="button"
      className="mt-8 w-full rounded-full bg-neutral-900 px-6 py-3 text-base font-medium text-white transition hover:bg-neutral-800"
      onClick={onStart}
    >
      {COPY.pricing.cta}
    </button>
  </div>
);

type FAQItem = {
  question: string;
  answer: string;
};

const FAQAccordion = ({ items }: { items: FAQItem[] }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const contentId = `faq-item-${index}`;
        return (
          <div
            key={item.question}
            className="rounded-2xl border border-neutral-200 bg-white px-6 py-4 shadow-sm"
          >
            <button
              type="button"
              className="flex w-full items-center justify-between text-left text-base font-medium text-neutral-900"
              aria-expanded={isOpen}
              aria-controls={contentId}
              onClick={() => setOpenIndex(isOpen ? null : index)}
            >
              <span>{item.question}</span>
              <span className="text-neutral-400">{isOpen ? '−' : '+'}</span>
            </button>
            <div
              id={contentId}
              className={`mt-3 text-sm text-neutral-600 ${isOpen ? 'block' : 'hidden'
                }`}
            >
              {item.answer}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface LandingPageProps {
  onEnterApp: () => void;
}

export function LandingPage({ onEnterApp }: LandingPageProps) {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaSubmitting, setBetaSubmitting] = useState(false);
  const [betaSubmitted, setBetaSubmitted] = useState(false);
  const [betaError, setBetaError] = useState<string | null>(null);
  const [betaForm, setBetaForm] = useState({
    email: '',
    trainingFrequency: '',
    rhythmBreakers: [] as string[],
    goalText: '',
    consent: false,
  });
  const { isAuthenticated } = useAuthStore();

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    onEnterApp();
  };

  const handleStart = () => {
    trackEvent('cta_pricing_click');
    navigate('/beta');
  };

  const resetBetaForm = () => {
    setBetaForm({
      email: '',
      trainingFrequency: '',
      rhythmBreakers: [],
      goalText: '',
      consent: false,
    });
    setBetaError(null);
    setBetaSubmitting(false);
    setBetaSubmitted(false);
  };

  const handleCloseBetaModal = () => {
    setShowBetaModal(false);
    resetBetaForm();
  };

  const handleOpenBetaModal = () => {
    setShowBetaModal(true);
  };

  const handleBetaSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBetaError(null);

    const email = betaForm.email.trim();
    const emailLower = email.toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLower);

    if (!emailValid) {
      setBetaError('Please enter a valid email.');
      return;
    }
    if (!betaForm.trainingFrequency) {
      setBetaError('Please select your training frequency.');
      return;
    }
    if (betaForm.rhythmBreakers.length === 0) {
      setBetaError('Please select what usually breaks your rhythm.');
      return;
    }
    if (!betaForm.consent) {
      setBetaError('Please confirm consent to be contacted.');
      return;
    }

    setBetaSubmitting(true);

    const payload = {
      email,
      trainingFrequency: betaForm.trainingFrequency,
      rhythmBreakers: betaForm.rhythmBreakers,
      goalText: betaForm.goalText.trim() || null,
      consent: betaForm.consent,
      source: 'landing',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      locale: typeof navigator !== 'undefined' ? navigator.language : null,
    };

    try {
      await api.post('/beta-applications/submit', payload);
      setBetaSubmitted(true);
    } catch (error) {
      console.error('Failed to submit beta application', error);
      setBetaError('Something went wrong. Please try again.');
    } finally {
      setBetaSubmitting(false);
    }
  };

  const sections = useMemo(
    () => ({
      problem: COPY.problem,
      howItWorks: COPY.howItWorks,
      whatThisIs: COPY.whatThisIs,
      proof: COPY.proof,
      faq: COPY.faq,
    }),
    []
  );

  return (
    <>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      <Dialog open={showBetaModal} onOpenChange={(open) => {
        if (open) {
          setShowBetaModal(true);
        } else {
          handleCloseBetaModal();
        }
      }}>
        <DialogContent className="max-w-lg">
          {betaSubmitted ? (
            <>
              <DialogHeader>
                <DialogTitle>{COPY.beta.successTitle}</DialogTitle>
                <DialogDescription>{COPY.beta.successBody}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="mt-6">
                <button
                  type="button"
                  className="rounded-full bg-neutral-900 px-6 py-2 text-sm font-medium text-white transition hover:bg-neutral-800"
                  onClick={handleCloseBetaModal}
                >
                  Close
                </button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleBetaSubmit} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{COPY.beta.title}</DialogTitle>
                <DialogDescription>{COPY.beta.intro}</DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="beta-email">{COPY.beta.form.emailLabel}</Label>
                <Input
                  id="beta-email"
                  type="email"
                  value={betaForm.email}
                  onChange={(event) =>
                    setBetaForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="beta-frequency">{COPY.beta.form.frequencyLabel}</Label>
                <select
                  id="beta-frequency"
                  value={betaForm.trainingFrequency}
                  onChange={(event) =>
                    setBetaForm((prev) => ({
                      ...prev,
                      trainingFrequency: event.target.value,
                    }))
                  }
                  className="flex h-10 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                  required
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  {COPY.beta.form.frequencyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                <Label>{COPY.beta.form.rhythmLabel}</Label>
                <div className="space-y-2">
                  {COPY.beta.form.rhythmOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={betaForm.rhythmBreakers.includes(option)}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setBetaForm((prev) => ({
                              ...prev,
                              rhythmBreakers: [...prev.rhythmBreakers, option],
                            }));
                          } else {
                            setBetaForm((prev) => ({
                              ...prev,
                              rhythmBreakers: prev.rhythmBreakers.filter(
                                (item) => item !== option
                              ),
                            }));
                          }
                        }}
                        className="h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
                      />
                      <span className="text-base text-neutral-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="beta-goal">{COPY.beta.form.goalLabel}</Label>
                <textarea
                  id="beta-goal"
                  value={betaForm.goalText}
                  onChange={(event) =>
                    setBetaForm((prev) => ({
                      ...prev,
                      goalText: event.target.value,
                    }))
                  }
                  className="min-h-[96px] w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="beta-consent"
                  type="checkbox"
                  checked={betaForm.consent}
                  onChange={(event) =>
                    setBetaForm((prev) => ({
                      ...prev,
                      consent: event.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
                />
                <Label htmlFor="beta-consent" className="text-sm text-neutral-600">
                  {COPY.beta.form.consentLabel}
                </Label>
              </div>

              {betaError && (
                <p className="text-sm text-red-600">{betaError}</p>
              )}

              <DialogFooter className="mt-2">
                <button
                  type="button"
                  className="rounded-full border border-neutral-200 px-6 py-2 text-sm font-medium text-neutral-700 transition hover:border-neutral-300 hover:text-neutral-900"
                  onClick={handleCloseBetaModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-neutral-900 px-6 py-2 text-sm font-medium text-white transition hover:bg-neutral-800 disabled:opacity-60"
                  disabled={betaSubmitting}
                >
                  {betaSubmitting ? 'Submitting...' : COPY.beta.form.submitLabel}
                </button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <div className="min-h-screen bg-white text-neutral-900 scroll-smooth" style={{ scrollPaddingTop: '80px' }}>
        <Navbar
          onStart={handleStart}
          onLogin={() => setShowAuthModal(true)}
          onEnterApp={onEnterApp}
          isAuthenticated={isAuthenticated}
        />
        <main className="pt-10">
          <Hero />

          <Section id="audience" className="bg-neutral-50">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-neutral-900 md:text-3xl">
                {COPY.audience.title}
              </h2>
              <p className="mt-2 text-lg text-neutral-600">
                {COPY.audience.subtitle}
              </p>
              <p className="mt-4 text-sm text-neutral-500">
                {COPY.audience.disclaimer}
              </p>
            </div>
          </Section>

          <Section id="value-props">
            <div className="grid gap-6 md:grid-cols-3">
              {COPY.valueProps.items.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100">
                    <item.icon className="h-5 w-5 text-neutral-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="problem" title={sections.problem.title} className="bg-neutral-50">
            <div className="max-w-3xl mx-auto">
              {sections.problem.bullets.map((item, index) => (
                <div
                  key={index}
                  className="mb-4 rounded-2xl border border-neutral-200 bg-white p-6 text-base text-neutral-700 shadow-sm"
                >
                  {item}
                </div>
              ))}
            </div>
          </Section>

          <Section
            id="what-this-is"
            title="What this is (and isn’t)"
            className="bg-neutral-50"
          >
            <div className="grid gap-6 rounded-3xl border border-neutral-200 bg-white p-8 shadow-sm md:grid-cols-2">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-neutral-400">
                  {sections.whatThisIs.leftTitle}
                </p>
                <ul className="mt-4 space-y-3 text-neutral-600">
                  {sections.whatThisIs.contrasts.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-neutral-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-neutral-400">
                  {sections.whatThisIs.rightTitle}
                </p>
                <ul className="mt-4 space-y-3 text-neutral-600">
                  {sections.whatThisIs.affirmations.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-neutral-900" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>

          <Section id="how-it-works" title="How it works">
            <div className="grid gap-6 md:grid-cols-3">
              {sections.howItWorks.map((step) => (
                <Card
                  key={step.title}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </Section>

          <Section id="proof" title={sections.proof.title} className="bg-neutral-50">
            <div className="max-w-3xl mx-auto space-y-6">
              <p className="text-base text-neutral-700">
                {sections.proof.description}
              </p>
              {/* <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3">
                  {sections.proof.workoutExample.title}
                </h3>
                <p className="text-sm text-neutral-600">
                  {sections.proof.workoutExample.description}
                </p>
              </div> */}
            </div>
          </Section>

          <Section id="closed-beta" title={COPY.beta.title}>
            <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-sm">
              <p className="text-base text-neutral-600">{COPY.beta.intro}</p>
              <ul className="mt-4 space-y-2 text-sm text-neutral-600">
                {COPY.beta.bullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-neutral-400" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Link
                  to="/beta"
                  className="inline-block rounded-full bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-neutral-800"
                >
                  {COPY.beta.cta}
                </Link>
              </div>
            </div>
          </Section>

          <Section id="pricing" title={COPY.pricing.title} className="bg-neutral-50">
            <div className="mx-auto max-w-2xl">
              <PricingCard onStart={handleStart} />
            </div>
          </Section>

          <Section id="faq" title="FAQ">
            <FAQAccordion items={sections.faq} />
          </Section>
        </main>
      </div>
    </>
  );
}
